(() => {
  const selectorGroups = [
    '.ny-native__hero-copy',
    '.ny-native__head',
    '.ny-native__collection',
    '.ny-native__trust article',
    '.ny-native__access-media',
    '.ny-native__access-copy',
    '.nyc__main > *',
    '#product-grid > .grid__item',
    '.product__media-wrapper',
    '.product__info-wrapper',
    '.nyon-page__hero > *',
    '.nyon-wholesale-journey li',
    '.nyon-wholesale-note__inner',
    '.nyon-page__about-hero > *',
    '.nyon-page__about-copy > *',
    '.nyon-page__grid > *',
    '.nyon-editorial-page__hero > *',
    '.nyon-editorial-page__feature > *',
    '.nyon-editorial-page__paths > *',
    '.nyon-editorial-page__steps > *',
    '.nyon-editorial-page__closing > *',
    '.nyp__intro > *',
    '.nyp__fact',
    '.nyp__details details',
    '.nyf__grid > *'
  ];

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let observer;
  const navControllers = new WeakMap();

  const reveal = (element) => element.classList.add('nyon-motion-in');

  const initializeCollectionNav = (scope = document) => {
    scope.querySelectorAll('[data-nyc-nav-root]').forEach((root) => {
      if (navControllers.has(root)) return;

      const nav = root.querySelector('[data-nyc-nav]');
      const indicator = root.querySelector('[data-nyc-indicator]');
      const response = root.querySelector('[data-nyc-response]');
      const responseText = root.querySelector('[data-nyc-response-text]');
      const links = Array.from(root.querySelectorAll('[data-nyc-nav-link]'));
      const active = links.find((link) => link.getAttribute('aria-current') === 'page');
      const initialResponse = responseText ? responseText.textContent.trim() : '';
      if (!nav || !indicator || !links.length) return;

      let responseTimer;
      let navigationTimer;

      const setResponse = (text, acknowledge = false) => {
        if (!response || !responseText || !text) return;
        window.clearTimeout(responseTimer);
        response.classList.add('is-changing');
        response.classList.toggle('is-acknowledged', acknowledge);
        responseTimer = window.setTimeout(() => {
          responseText.textContent = text;
          response.classList.remove('is-changing');
        }, reduceMotion.matches ? 0 : 105);
      };

      const moveIndicator = (link, animate = true) => {
        if (!link) {
          indicator.style.width = '0px';
          return;
        }
        if (!animate) nav.classList.remove('is-ready');
        indicator.style.width = `${link.offsetWidth}px`;
        indicator.style.transform = `translate3d(${link.offsetLeft}px,0,0)`;
        if (!animate) requestAnimationFrame(() => nav.classList.add('is-ready'));
      };

      const restoreActive = () => {
        if (nav.classList.contains('is-committing')) return;
        moveIndicator(active);
        setResponse(active ? (active.dataset.response || active.textContent.trim()) : initialResponse);
      };

      const preview = (link) => {
        moveIndicator(link);
        setResponse(link.dataset.response || link.textContent.trim());
      };

      const commit = (event, link) => {
        if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target === '_blank') return;
        if (link.href === window.location.href) return;
        event.preventDefault();
        window.clearTimeout(navigationTimer);
        moveIndicator(link);
        nav.classList.add('is-committing');
        setResponse(`Opening ${link.textContent.trim()}…`, true);
        document.documentElement.classList.add('nyc-navigation-leaving');
        navigationTimer = window.setTimeout(() => {
          window.location.assign(link.href);
        }, reduceMotion.matches ? 0 : 320);
      };

      links.forEach((link, index) => {
        link.addEventListener('pointerenter', () => preview(link));
        link.addEventListener('focus', () => preview(link));
        link.addEventListener('click', (event) => commit(event, link));
        link.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          event.preventDefault();
          const direction = event.key === 'ArrowRight' ? 1 : -1;
          links[(index + direction + links.length) % links.length].focus();
        });
      });

      nav.addEventListener('pointerleave', restoreActive);
      nav.addEventListener('focusout', (event) => {
        if (!nav.contains(event.relatedTarget)) restoreActive();
      });

      const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(() => moveIndicator(active, false)) : null;
      resizeObserver?.observe(nav);
      window.addEventListener('resize', restoreActive, { passive: true });
      moveIndicator(active, false);
      document.fonts?.ready.then(() => {
        if (root.isConnected) moveIndicator(active, false);
      });

      navControllers.set(root, {
        restore() {
          nav.classList.remove('is-committing');
          if (response) response.classList.remove('is-acknowledged', 'is-changing');
          moveIndicator(active, false);
          if (responseText) responseText.textContent = active ? (active.dataset.response || active.textContent.trim()) : initialResponse;
        },
        destroy() {
          window.clearTimeout(responseTimer);
          window.clearTimeout(navigationTimer);
          resizeObserver?.disconnect();
          document.documentElement.classList.remove('nyc-navigation-leaving');
        }
      });
    });
  };


  // Shopify Forms renders in an open shadow root. Add presentation only;
  // leave the app's validation, submission and disabled states in control.
  let wholesaleFormController;
  const initializeWholesaleFormMotion = () => {
    const source = document.querySelector('[data-nyon-wholesale-motion-styles]');
    const main = source?.closest('main');
    if (!source || !main) return;
    if (wholesaleFormController?.source === source) {
      wholesaleFormController.refresh();
      return;
    }
    wholesaleFormController?.destroy();
    const roots = new Map();
    let retryTimer;
    let attempts = 0;
    let destroyed = false;
    const applyStyle = (root) => {
      if (root.querySelector('#nyon-wholesale-form-motion')) return;
      const style = source.content.querySelector('style');
      if (style) root.appendChild(style.cloneNode(true));
    };
    const mount = () => {
      window.clearTimeout(retryTimer);
      if (destroyed || !source.isConnected) return;
      const hosts = Array.from(main.querySelectorAll('shopify-forms-embed'));
      hosts.forEach((host) => {
        const root = host.shadowRoot;
        if (!root) return;
        applyStyle(root);
        if (!roots.has(root)) {
          const rootObserver = new MutationObserver(() => applyStyle(root));
          rootObserver.observe(root, { childList: true });
          roots.set(root, rootObserver);
        }
      });
      roots.forEach((rootObserver, root) => {
        if (!root.host.isConnected) { rootObserver.disconnect(); roots.delete(root); }
      });
      if ((!hosts.length || hosts.some((host) => !host.shadowRoot)) && attempts++ < 60) {
        retryTimer = window.setTimeout(mount, 250);
      }
    };
    const hostObserver = new MutationObserver(mount);
    hostObserver.observe(main, { childList: true, subtree: true });
    wholesaleFormController = {
      source,
      refresh: mount,
      destroy() {
        destroyed = true;
        window.clearTimeout(retryTimer);
        hostObserver.disconnect();
        roots.forEach((rootObserver) => rootObserver.disconnect());
        roots.clear();
      }
    };
    window.customElements?.whenDefined('shopify-forms-embed').then(mount);
    mount();
  };

  const buildObserver = () => {
    if (observer || !('IntersectionObserver' in window) || reduceMotion.matches) return;
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        reveal(entry.target);
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -5% 0px' });
  };

  const initialize = (scope = document) => {
    initializeCollectionNav(scope);
    initializeWholesaleFormMotion();
    buildObserver();
    const elements = Array.from(scope.querySelectorAll(selectorGroups.join(',')))
      .filter((element) => !element.classList.contains('nyon-motion-target'));

    elements.forEach((element, index) => {
      element.classList.add('nyon-motion-target');
      const delay = element.matches('.nyon-wholesale-journey li')
        ? Array.from(element.parentElement.children).indexOf(element) * 90
        : Math.min(index % 4, 3) * 48;
      element.style.setProperty('--nyon-delay', `${delay}ms`);

      if (reduceMotion.matches || !observer) {
        reveal(element);
        return;
      }

      const rect = element.getBoundingClientRect();
      if (rect.top < window.innerHeight * .94 && rect.bottom > 0) {
        requestAnimationFrame(() => reveal(element));
      } else {
        observer.observe(element);
      }
    });
  };

  const revealAll = () => {
    document.querySelectorAll('.nyon-motion-target').forEach(reveal);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  window.addEventListener('pageshow', (event) => {
    document.documentElement.classList.remove('nyc-navigation-leaving');
    document.querySelectorAll('[data-nyc-nav-root]').forEach((root) => navControllers.get(root)?.restore());
    if (event.persisted) initialize();
  });
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (wholesaleFormController && event.target.contains(wholesaleFormController.source)) {
      wholesaleFormController.destroy();
      wholesaleFormController = undefined;
    }
    event.target.querySelectorAll('[data-nyc-nav-root]').forEach((root) => {
      navControllers.get(root)?.destroy();
      navControllers.delete(root);
    });
  });
  reduceMotion.addEventListener?.('change', (event) => {
    if (event.matches) revealAll();
  });
})();
